import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useModal } from 'src/components/Modal'
import { Form, Formik } from 'formik'
import FormField from 'src/components/FormField'
import Container from 'src/components/Container'
import dayjs from 'dayjs'
import {
	useUsersGetAllQuery,
	useRecentBulletinsQuery,
	useRecentResourcesQuery,
	UsersGetAllQuery,
	useAddNominationMutation,
	useGetNominationsQuery,
	useRemoveNominationMutation,
	useSendNominationEmailMutation
} from 'shared/schema'

import LinkCard from 'src/components/LinkCard'
import Masthead from 'src/components/Header'
import { useSiteContext } from 'src/components/AppWrapper'
import HeroCarousel from './HeroCarousel'
import FrequentlyAccessed from './FrequentlyAccessed'
import SocialFeed from './SocialFeed'
import {
	SectionHeading,
	HeroQuoteBackground,
	QuoteContainer,
	StatementBox,
	Statement,
	CoreValuesContainer,
	CoreValues,
	CoreValuesColumn,
	CoreValue,
	HomeColumns,
	HomeSideColumn,
	CheersBox,
	CheersLogo,
	CheersSubhead,
	CheersNominateButton,
	RecentNominationsHeading,
	NominationsGrid,
	ResourceRows,
	FavoritesHeading,
	FavoritesPin,
	FavoriteRows,
	NoFavoritesText,
	MoreResourcesLink
} from './styled'
import CheersImage from 'url:./assets/cheers.png'

import { NomCard } from 'src/components/PersonCard'
import { useAuthGate } from 'src/components/AuthGate'
import _ from 'lodash'
import { Tabs } from 'react-tabs'
import { Tab as ReactTab, TabList as ReactTabList, TabPanel as ReactTabsPanel } from 'react-tabs'
export const TabList = styled(ReactTabList)`
	display: flex;
	list-style: none;
	padding-left: 0;
`

export const Tab = styled(ReactTab)`
	padding: 15px 10px;
	margin-right: 10px;
	&:hover {
		cursor: pointer;
		color: ${({ theme }) => theme.colors.primary};
	}
	&.active {
		font-weight: 500;
		color: ${({ theme }) => theme.colors.primary};
		border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
	}
`

export const TabContent = styled(ReactTabsPanel)``

function HomeRoute() {
	const { user } = useAuthGate()
	const { siteMeta } = useSiteContext()

	const { data: newsData, loading: newsLoading } = useRecentBulletinsQuery()
	const { data: resourcesData } = useRecentResourcesQuery()

	const favoriteResources = user.favoriteResources.map((fav) => fav.resource)

	const bulletins = newsData?.bulletins ?? []
	const homeMastheadUrl = siteMeta.pages.find((page) => page.slug === 'home')?.masthead?.publicUrl

	const { loading: usersLoading, data: usersData } = useUsersGetAllQuery()

	const userOptions = (usersData?.users ?? []).map((u: UsersGetAllQuery['users'][number]) => ({
		label: `${u.firstName} ${u.lastName}`,
		value: `${u.id}`
	}))
	const categoryOptions = [
		{ value: 'Culture', label: 'Culture' },
		{ value: 'Safety', label: 'Safety' },
		{ value: 'Sustainability', label: 'Sustainability' },
		{ value: 'Initiative', label: 'Initiative', explainer: 'Went the extra mile' },
		{
			value: 'Mentoring',
			label: 'Mentoring',
			explainer: 'Provided guidance and support to someone outside the scope of their job'
		},
		{ value: 'Innovation', label: 'Innovation', explainer: 'Improved a process' },
		{
			value: 'Community',
			label: 'Community',
			explainer:
				'Initiated or participated in a community project on behalf of Nibbi, or went the extra mile to build relationships in the communities where we work'
		},
		{
			value: 'Excellence',
			label: 'Excellence',
			explainer: 'Outstanding result such as financial, schedule, quality, or resolving a sticky issue'
		},
		{
			value: 'Client Service',
			label: 'Client Service',
			explainer:
				'Exceeded client expectations, or Nibbi awarded a new job as a direct result of a person or team effort'
		}
	]

	const [currentTab, setCurrentTab] = useState(0)
	const [singleNom, setSingleNom] = useState(null)

	const { data: nominationsRawData } = useGetNominationsQuery({
		skip: !(usersData?.users.length > 0)
	})
	const nominationsLoaded = Boolean(nominationsRawData?.v2_nominations)

	const usersById = _.keyBy(usersData?.users || [], 'id')

	let nominations = []
	let tabNominations = []

	if (nominationsLoaded) {
		const thisMonthStart = dayjs().startOf('month')
		const thisMonthEnd = dayjs().endOf('month')
		const lastMonthStart = dayjs().subtract(1, 'month').startOf('month')
		const lastMonthEnd = dayjs().subtract(1, 'month').endOf('month')

		nominations = nominationsRawData.v2_nominations.map((n) => {
			return {
				id: n.id,
				user: usersById[n.to_user_id] || { firstName: n.to_name },
				values: n
			}
		})
		if (singleNom) {
			tabNominations = nominations.filter((n) => n.id === singleNom)
		} else if (currentTab === 0) {
			tabNominations = nominations.filter(
				(nom) =>
					dayjs(nom.values.created_at).isAfter(thisMonthStart) &&
					dayjs(nom.values.created_at).isBefore(thisMonthEnd)
			)
		} else if (currentTab === 1) {
			tabNominations = nominations.filter(
				(nom) =>
					dayjs(nom.values.created_at).isAfter(lastMonthStart) &&
					dayjs(nom.values.created_at).isBefore(lastMonthEnd)
			)
		} else {
			tabNominations = nominations
		}
	}

	const [addNominationMutation] = useAddNominationMutation({
		refetchQueries: ['getNominations'],
		awaitRefetchQueries: true
	})
	const [sendNominationEmail] = useSendNominationEmailMutation()

	const [removeNominationMutation] = useRemoveNominationMutation({
		refetchQueries: ['getNominations'],
		awaitRefetchQueries: true
	})

	const nomModalHeader = singleNom ? 'Cheers Nomination' : 'Cheers Nominations'

	const [allNomsModal, setShowAllNominations] = useModal({
		tall: true,
		header: nomModalHeader,
		onClose: () => {
			setSingleNom(null)
		},
		children: (
			<div>
				{!singleNom && (
					<Tabs
						style={{ paddingBottom: '1rem' }}
						selectedIndex={currentTab}
						selectedTabClassName="active"
						onSelect={(index) => {
							setCurrentTab(index)
						}}
					>
						<TabList>
							<Tab>This Month</Tab>
							<Tab>Last Month</Tab>
							<Tab>All Time</Tab>
						</TabList>
						<TabContent />
						<TabContent />
						<TabContent />
					</Tabs>
				)}

				{tabNominations.length === 0 && (
					<div>
						{currentTab === 0 && <em>There have been no nominations this month.</em>}
						{currentTab === 1 && <em>There were no nominations last month.</em>}
						{currentTab === 2 && <em>There have been no nominations.</em>}
					</div>
				)}
				{tabNominations.map((nom) => (
					<NomCard
						key={nom.id}
						person={nom.user}
						values={nom.values}
						usersById={usersById}
						full
						onRemove={async () => {
							if (confirm('Are you sure you want to remove this nomination?')) {
								await removeNominationMutation({
									variables: {
										_eq: nom.id
									}
								})
							}
						}}
					/>
				))}
			</div>
		)
	})

	const defaultNominatingName = '...'
	const [nominatingName, setNominatingName] = useState(defaultNominatingName)

	const [nominationModal, setNominationModal, nominationModalOpen] = useModal({
		tall: true,
		header: `Cheers to ${nominatingName}`,
		children: (
			<Formik
				initialValues={{
					userId: { value: -1 },
					project: '',
					category: { value: '' },
					description: ''
				}}
				validate={(values) => {
					const errors: any = {}

					if (!values.userId || values.userId?.value === -1) {
						errors.userId = 'Choose or type in the name of a person'
					}
					if (values.category.value === '') {
						errors.category = 'Choose a category'
					}

					return errors
				}}
				onSubmit={async (values) => {
					const toUserId = +values.userId.value > 0 ? +values.userId.value : null
					let toUserName = ''
					if (!toUserId) {
						toUserName = values.userId.value.toString() || ''
					}
					await addNominationMutation({
						variables: {
							category: values.category.value,
							description: values.description,
							from_user_id: user.id,
							project: values.project,
							to_name: toUserName,
							to_user_id: toUserId
						}
					})
					const userId = +values.userId?.value
					const { email, supervisorId, firstName, lastName } = usersById[userId] ?? {}
					let mgrEmail: string | null = null

					if (supervisorId) {
						const supervisorEmail = usersById[supervisorId]?.email
						if (supervisorEmail) {
							mgrEmail = supervisorEmail
						}
					}
					if (userId > 0 && email) {
						const emailVariables = {
							to: email,
							mgr_to: mgrEmail,
							category: values.category.value,
							html: values.description,
							name: `${user.firstName || ''} ${user.lastName || ''}`,
							to_name: `${firstName || ''} ${lastName || ''}`
						}
						if (location.host.endsWith('1234')) {
							emailVariables.to = `test+${emailVariables.to.split('@')[0]}@mweber.xyz`

							if (emailVariables.mgr_to) {
								emailVariables.mgr_to = `test+${emailVariables.mgr_to.split('@')[0]}@mweber.xyz`
							}
						}
						await sendNominationEmail({
							variables: emailVariables
						})
					}
					setNominationModal(false)
				}}
			>
				{({ values, setFieldValue, errors, touched }) => {
					return (
						<Form>
							<FormField
								name="userId"
								type="select2-create"
								label="Who are you nominating?"
								options={userOptions}
								onChange={(value) => {
									const name = value?.label ? `${value?.label}!` : defaultNominatingName
									if (name !== nominatingName) {
										setNominatingName(name)
									}
								}}
								formatCreateLabel={(name) => (
									<>
										<em>Add</em> {name}
									</>
								)}
								placeholder="If not listed, type a full name and click Add"
							/>
							{touched.userId && errors.userId && (
								<em style={{ fontSize: '80%', color: 'red' }}>{errors.userId}</em>
							)}
							<FormField
								name="category"
								type="select2"
								label="Which category are you nominating for?"
								options={categoryOptions}
								formatOptionLabel={(data, { context }) => {
									return (
										<div>
											<div>{data.label}</div>
											{context === 'menu' && data.explainer && (
												<div
													style={{
														fontSize: '75%',
														color: values.category.value === data.value ? 'white' : '#777'
													}}
												>
													{data.explainer}
												</div>
											)}
										</div>
									)
								}}
							/>
							{touched.category && errors.category && (
								<em style={{ fontSize: '80%', color: 'red' }}>{errors.category}</em>
							)}

							<FormField
								name="description"
								type="richText"
								label="Describe what inspired your nomination"
								value={values.description}
								placeholder=""
								onChange={(val) => setFieldValue('description', val)}
							/>

							<FormField type="formButtons" onCancel={() => setNominationModal(false)} hideBorderTop />
						</Form>
					)
				}}
			</Formik>
		)
	})

	useEffect(() => {
		if (!nominationModalOpen && nominatingName !== defaultNominatingName) {
			setNominatingName(defaultNominatingName)
		}
	}, [nominatingName, nominationModalOpen])

	return (
		<>
			{nominationModal}
			{allNomsModal}
			{bulletins.length > 0 ? (
				<HeroCarousel bulletins={bulletins} />
			) : (
				!newsLoading && <Masthead imageUrl={homeMastheadUrl} title="Home" hideTitle />
			)}
			<Container>
				<HomeColumns>
					{nominationsLoaded && (
						<CheersBox>
							<CheersLogo src={CheersImage} alt="Cheers!" />
							<CheersSubhead>Give a shout out to those who have gone the extra mile!</CheersSubhead>
							<CheersNominateButton type="button" onClick={() => setNominationModal(true)}>
								Nominate Someone
							</CheersNominateButton>

							{nominations.length > 0 && (
								<>
									<RecentNominationsHeading>Recent Nominations</RecentNominationsHeading>
									<NominationsGrid>
										{nominations.slice(0, 2).map((nom) => (
											<NomCard
												key={nom.id}
												person={nom.user}
												values={nom.values}
												usersById={usersById}
												excerpt
												clicked={() => {
													setSingleNom(nom.id)
													setShowAllNominations(true)
												}}
											/>
										))}
									</NominationsGrid>
								</>
							)}
						</CheersBox>
					)}
					<HomeSideColumn>
						<SectionHeading>Resources</SectionHeading>
						<ResourceRows>
							{(resourcesData?.resources || []).map((resource) => (
								<LinkCard key={`resources:${resource.id}`} data={resource} $displaySize="lg" />
							))}
						</ResourceRows>
						<FavoritesHeading>
							<FavoritesPin />
							Favorites
						</FavoritesHeading>
						{favoriteResources.length > 0 ? (
							<FavoriteRows>
								{favoriteResources.map((resource) => (
									<LinkCard key={`resources:${resource.id}`} data={resource} $displaySize="lg" />
								))}
							</FavoriteRows>
						) : (
							<NoFavoritesText>You do not have any favorite resources yet.</NoFavoritesText>
						)}
						<MoreResourcesLink to="/resources">More resources</MoreResourcesLink>
					</HomeSideColumn>
				</HomeColumns>
				<FrequentlyAccessed />
				<SocialFeed />
			</Container>
			<HeroQuoteBackground>
				<QuoteContainer>
					<StatementBox>
						<Statement>
							<h4>Mission Statement</h4>
							<p>
								Serving our clients and community by building safely with integrity, innovation and
								quality.
							</p>
						</Statement>
						<Statement>
							<h4>Vision Statement</h4>
							<p>
								Be the well-respected builder and employer of choice, focused on safety, quality and
								profitability.
							</p>
						</Statement>
					</StatementBox>
				</QuoteContainer>
			</HeroQuoteBackground>
			<Container>
				<CoreValuesContainer>
					<h3>Our Core Values</h3>
					<CoreValues>
						<CoreValuesColumn>
							<CoreValue>
								<h4>Safety</h4>
								<p>
									We are firmly committed to maintaining the safest possible jobsite for our employees,
									subcontractors, clients and the community. We constantly work to improve awareness
									and accident prevention through continual staff training and highly detailed pre-task
									planning.
								</p>
							</CoreValue>
							<CoreValue>
								<h4>Honesty & Integrity</h4>
								<p>
									Nibbi operates with the highest professional and ethical standards. We stand behind
									our work and do what is best for each project, allowing our corporate culture and
									values to guide our conduct.
								</p>
							</CoreValue>
						</CoreValuesColumn>
						<CoreValuesColumn>
							<CoreValue>
								<h4>Client Focus</h4>
								<p>
									Our highest priority is helping our clients find solutions to achieve their
									objectives. We continuously strive to better serve our clients through collaboration,
									innovation and efficiency.
								</p>
							</CoreValue>
							<CoreValue>
								<h4>Social Responsibility</h4>
								<p>
									We genuinely care for the communities in which we live and work. We give our time,
									talent and resources to local communities and organizations in need. We practice
									sustainable and green construction processes and are environmentally conscious in
									our everyday work lives.
								</p>
							</CoreValue>
							<CoreValue>
								<h4>Family Culture</h4>
								<p>
									We are proud of our emphasis on fostering a strong sense of family through caring,
									mutual respect and promoting a strong work / life balance.
								</p>
							</CoreValue>
						</CoreValuesColumn>
						<CoreValuesColumn>
							<CoreValue>
								<h4>Opportunity</h4>
								<p>
									We provide dynamic career opportunities and mentoring for our employees. We encourage
									leadership training and continuing education at all levels. We continuously strive
									to recruit and develop the best talent.
								</p>
							</CoreValue>
							<CoreValue>
								<h4>Financial Stability</h4>
								<p>
									Nibbi believes great companies are built on sound financial practices and a history
									of solid financial results. We are committed to effectively managing our financial
									resources and earning a consistent profit to support our clients’ needs and achieve
									our long-term goals.
								</p>
							</CoreValue>
						</CoreValuesColumn>
					</CoreValues>
				</CoreValuesContainer>
			</Container>
		</>
	)
}

export default HomeRoute
